/**
 * Раздел «Финансы» портала — этап P3 (§2, §4, §6 ТЗ портала).
 *
 * Экраны — **импортом из дашборда финансов** (`@finance`), а не копией:
 * «Обзор», «Продажи», «Себестоимость», «Меню», «Чеки», «Контроль»,
 * «Данные» — те же файлы, что на `finance.`. Портал добавляет только
 * вкладки, адрес и обёртку со стилями дашборда.
 *
 * «Настройки» дашборда сюда не переезжают: подписки и доступ — разделы
 * портала «Настройки» и «Команда». Из них в «Финансах» остаются пороги и
 * цели — «Контроль → Пороги и цели», потому что по ним и срабатывает лента
 * «Контроля».
 *
 * Экраны и библиотека графиков грузятся лениво — отдельным файлом, только
 * когда раздел открыт: остальным разделам портала Recharts не нужен.
 */

import { Suspense, lazy } from 'react';
import { Denied, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { canSee } from '../../nav';
import { navigate, useRoute } from '../../router';
import { installMockFinanceApi } from './api';
import { financePath, openTabs, readFinance } from './routes';
import type { FinanceState } from './routes';
import './finance.css';

installMockFinanceApi();

const Screens = lazy(() => import('./Screens'));

export default function Finance({ nav }: { nav: Navigation }) {
  const route = useRoute();
  if (!canSee('finance', nav)) return <Denied />;

  const tabs = openTabs(nav.services.finance?.rights, nav.user.is_host);
  const read = readFinance(route.path, route.params);
  // Закрытая вкладка по прямой ссылке — первая открытая, а не пустой экран.
  const state = tabs.some((tab) => tab.code === read.tab)
    ? read
    : { ...read, tab: tabs[0]?.code ?? read.tab };
  const go = (next: Partial<FinanceState>) => navigate(financePath({ ...state, ...next }));

  return (
    <div className="page finance-page">
      <header className="page__head finance-page__head">
        <h1 className="page__title">Финансы</h1>
      </header>
      <div className="finance-tabs" role="tablist" aria-label="Экраны финансов">
        {tabs.map((tab) => (
          <button
            key={tab.code}
            type="button"
            role="tab"
            aria-selected={tab.code === state.tab}
            className={`chip${tab.code === state.tab ? ' chip--active' : ''}`}
            onClick={() => go({ tab: tab.code, view: 'feed' })}
          >
            {tab.title}
          </button>
        ))}
      </div>
      <div className="finance-scope">
        <Suspense fallback={<Loading />}>
          <Screens state={state} go={go} />
        </Suspense>
      </div>
    </div>
  );
}
